import * as React from 'react';

import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useLocation } from "react-router-dom";
import { useAsyncEffect } from "../../common/hooks";
import protectAxios from "../../api/axios";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const getPasswordStrength = (password: string) => {
  let strength = 0;
  if (password.length > 5) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;
  if (password.length > 10) strength++;
  return strength;
};

interface SubscriptionResvolveResponse {
  id : string;
  subscriptionName: string;
  offerId : string;
  planId : string;
  quantity : number;
  subscription :{ 
    id: string;
    name: string;
    publisherId: string;
    offerId: string; 
    planId: string;
    quantity: number;
    beneficiary:{
      emailId: string;
      objectId: string;
      tenantId: string;
      puid: string;
    },
    isFreeTrial: boolean;    
    autoRenew: boolean;
    
  }
  companyName: string;
}

export const SignupMarketplacePage: React.FC = (props) => {

  const query = useQuery();
  const publishersToken = query.get('token'); 

  const [activeStep, setActiveStep] = React.useState(0);
  const [error, setError] = React.useState({ password: '', confirm: '', email: '' });

  const [subscription, setSubscription] = React.useState<SubscriptionResvolveResponse>();
  
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [emailAddress, setEmailAddress] = React.useState('');
  const [companyName, setCompanyName] = React.useState<string | undefined>();

  const [activated, setActivated] = React.useState(false);
  const [activationError, setActivationError] = React.useState('');

  const strength = getPasswordStrength(password);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateLive = (newPassword: string, newConfirm: string, emailAddress: string) => {
    const newError = { password: '', confirm: '', email: '' };

    if (!emailAddress)
    {
      newError.email = 'Email is required.';
    }

    if (!newPassword) {
      newError.password = 'Password is required.';
    } else if (strength < 5) {
      newError.password = 'Password supply a strong password';
    }

    if (!newConfirm) {
      newError.confirm = 'Please confirm your password.';
    } else if (newPassword && newPassword !== newConfirm) {
      newError.confirm = 'Passwords do not match.';
    }

    setError(newError);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validateLive(newPassword, confirm, emailAddress);
  };

  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyName(e.target.value);
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirm = e.target.value;
    setConfirm(newConfirm);
    validateLive(password, newConfirm, emailAddress);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmailAddress(newEmail);
    validateLive(password, confirm, emailAddress);
  };

  const isCompanyValid = () => {
    return companyName != ''
  }

  const { loading } = useAsyncEffect(async () => {
    const newSubscription = await protectAxios
        .get(`/companyqueries/resolve?publishersToken=${publishersToken}`)
        .then((resp) => resp.data);
  
      setCompanyName(newSubscription.companyName);
      setEmailAddress(newSubscription.subscription.beneficiary.emailId);

      setSubscription(newSubscription);
  }, []);

  const activateSubscription = async () => {

    await protectAxios
        .post(`/companycommands/activatesubscription`, {
          subscriptionId : subscription?.id,
          companyName: companyName,
          emailAddress: emailAddress,
          password: password
        })
        .then((resp) => {
          setActivated(true);
        })
        .catch(function(error) {
          setActivationError(error);
        });
  }

  const handleActivate = () => {
    activateSubscription();
  };

  return (
    <>
      <Box
            sx={{
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              p: 2
            }}
          >
            <Box sx={{ width: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            
              {/* Logo at the top */}
              <Box sx={{mb:2}}>
                <img src="/logo.png" alt="Logo" style={{ height: 30, paddingBottom:0 }} />
              </Box>

              { loading ? 
                <>
                  <CircularProgress sx={{mb:2}}/>
                  <Typography>Loading your subscription, please wait.....</Typography>
                </> : 
                <>
                  { activated ? 
                  <>
                    <Box sx={{ mb:2, mt:1  }}>
                      <Typography gutterBottom sx={{color: 'grey.600'}}>
                        Your subscription is now activated.  please login below
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => { window.location.href = "/bff/login"; }}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Login
                      </Button>
                  </Box>
                  </> :
                  <>
                    { (activationError === '') ? 
                      <>
                        <Box sx={{ mb:2, mt:1  }}>
                          <Typography gutterBottom sx={{color: 'grey.600'}}>
                            Welcome and thank you for your purchase, please see the following details to confirm and
                            when you are ready click continue.
                          </Typography>
                        </Box>
              
                        <Stepper activeStep={activeStep} orientation="vertical" sx={{width:'100%'}}>

                          <Step key='validatepurchase'>
                            <StepLabel>Validate your purchase</StepLabel>
                            <StepContent> 
                              <Typography>Subscription name</Typography>
                              <Typography sx={{mb:1}}><b>{subscription?.subscriptionName}</b></Typography>
                              <Typography>Offer purchased</Typography>
                              <Typography sx={{mb:1}}><b>{subscription?.offerId}</b></Typography>
                              <Typography>Quantity purchased</Typography>
                              <Typography sx={{mb:1}}><b>{subscription?.quantity}</b></Typography>
                              <Typography>Product will auto renew</Typography>
                              <Typography sx={{mb:1}}><b>{subscription?.subscription.autoRenew  ? 'Yes' : 'No'}</b></Typography>
                              <Typography>Product includes a free trial</Typography>
                              <Typography sx={{mb:1}}><b>{subscription?.subscription.isFreeTrial ? 'Yes' : 'No'}</b></Typography>

                              <Box sx={{ mb: 2 }}>
                                  <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Continue
                                  </Button>
                                </Box>
                            </StepContent>
                          </Step>

                          <Step key='confirmcompany'>
                            <StepLabel>Confirm your company details</StepLabel>
                            <StepContent> 
                              <Typography sx={{ mt:1,fontSize:'small' }}>
                                                Your company name
                                              </Typography>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                margin="none"
                                                placeholder="Enter your company name"
                                                InputProps={{
                                                  sx: { mt: 0 }, // no margin-top
                                                }}
                                                onChange={handleCompanyNameChange}
                                                value={companyName}
                                              />
                              <Box sx={{ mb: 2 }}>
                                  <Button
                                    disabled={!isCompanyValid()}
                                    variant="contained"
                                    onClick={handleNext}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Continue
                                  </Button>
                                  <Button
                                    
                                    onClick={handleBack}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Back
                                  </Button>
                                </Box>
                            </StepContent>
                          </Step>

                          <Step key='confirmprimarycontact'>
                            <StepLabel>Confirm primary contact details</StepLabel>
                            <StepContent> 

                              <Typography sx={{ mt:1,fontSize:'small' }}>
                                Primary contact email address
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                margin="none"
                                placeholder="Enter your primary contact email"
                                InputProps={{
                                  sx: { mt: 0 }, // no margin-top
                                }}
                                onChange={handleEmailChange}
                                value={emailAddress}
                              />

                              <Typography sx={{ mt:1,mb:1,fontSize:'small' }}>Password</Typography>
                                          
                              <TextField sx={{ mb:1 }} fullWidth label="Password" type="password" 
                                size="small"      
                                margin="dense"
                                value={password}
                                onChange={handlePasswordChange}
                                error={!!error.password}
                                helperText={error.password}
                              />
                              
                              <TextField 
                                fullWidth 
                                label="Confirm Password" 
                                type="password" 
                                margin="dense" 
                                size="small"      
                                onChange={handleConfirmChange}
                                error={!!error.confirm}
                                helperText={error.confirm}
                                InputProps={{
                                  sx: {
                                    mt:0,
                                    // Custom styles only if in error
                                    ...(error.password && {
                                      mt:1
                                    })
                                }}}
                              />
                              
                              {/* Password strength dots */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  mt: 1,
                                  justifyContent: 'center',
                                }}
                              >
                                {[...Array(5)].map((_, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      width: 50,
                                      height: 10,
                                      borderRadius: '20%',
                                      bgcolor: index < strength ? 'primary.main' : 'grey.300',
                                      transition: 'background-color 0.3s ease',
                                    }}
                                  />
                                ))}
                              </Box>
                  
                              <Typography sx={{ mt:1, mb:1, color:'grey.500' }}>
                                Use 10+ chars including capital, digit and lowercase letters  
                              </Typography> 

                              <Box sx={{ mb: 2 }}>
                                  <Button
                                    disabled={!(
                                      password != '' && 
                                      confirm != '' && 
                                      emailAddress != '' &&
                                      error.confirm == '' &&
                                      error.email == '' &&
                                      error.password == ''
                                    )}
                                    variant="contained"
                                    onClick={handleNext}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Continue
                                  </Button>
                                  <Button
                                    
                                    onClick={handleBack}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Back
                                  </Button>
                                </Box>
                            </StepContent>
                          </Step>

                          <Step key='activate'>
                            <StepLabel>Activate your subscription</StepLabel>
                            <StepContent> 
                              <Box sx={{ mb: 2 }}>
                                  <Button
                                    variant="contained"
                                    onClick={handleActivate}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Activate your subscription
                                  </Button>
                                  <Button
                                    
                                    onClick={handleBack}
                                    sx={{ mt: 1, mr: 1 }}
                                  >
                                    Back
                                  </Button>
                                </Box>
                            </StepContent>
                          </Step>
                        </Stepper>                    
                      </> : 
                      <>
                        <Box sx={{ mb:2, mt:1  }}>
                          <Typography gutterBottom sx={{color: 'grey.600'}}>
                            We apologise, there was an issue activating your subscription.
                            We will look into this straight away and get back to you asap.
                          </Typography>
                        </Box>
                      </>
                    }
                  </>
                  }
                  
                </>
              }
              </Box>
          </Box>
    </>
  )
}




